import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import PrintSettings from "../components/PrintSettings";
import { SettingsSurface } from "@/features/settings/components/SettingsSurface";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Building2, Upload, Loader2, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { companyAPI, getApiErrorPayload } from "@/lib/api";
import { Loading } from "@/components/ui/loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUnsavedChangesGuard, useIsDirty } from "@/hooks/useUnsavedChangesGuard";
import { usePermission } from "@/hooks/usePermission";
import BranchesSection from "../components/BranchesSection";
import SubscriptionSection from "../components/SubscriptionSection";
import { confirmAction } from "@/components/ui/confirm-dialog";

/** Logo from API may be `/uploads/...` or absolute URL; editing may hold a data URL preview. */
function resolveCompanyLogoSrc(logo?: string | null): string | undefined {
	if (logo == null || String(logo).trim() === "") return undefined;
	const s = String(logo).trim();
	if (s.startsWith("data:") || s.startsWith("blob:") || s.startsWith("http://") || s.startsWith("https://")) {
		return s;
	}
	const baseUrl = (process.env.REACT_APP_API_URL || "http://localhost:3001/api").replace(/\/api\/?$/, "");
	return s.startsWith("/") ? `${baseUrl}${s}` : `${baseUrl}/${s}`;
}

export type DocumentThemeV1 = {
	version: number;
	preset: "modern" | "minimal" | "bold";
	primaryColor: string;
	accentColor: string;
	headerLayout: "logoLeft" | "logoCenter";
	showLogo: boolean;
	showTaxRow: boolean;
	footerNote: string;
	tableDensity: "comfortable" | "compact";
};

const DEFAULT_DOCUMENT_THEME: DocumentThemeV1 = {
	version: 1,
	preset: "modern",
	primaryColor: "#1c4585",
	accentColor: "#0ea5e9",
	headerLayout: "logoLeft",
	showLogo: true,
	showTaxRow: true,
	footerNote: "",
	tableDensity: "comfortable",
};

function mergeDocumentTheme(raw: unknown): DocumentThemeV1 {
	const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
	return {
		...DEFAULT_DOCUMENT_THEME,
		...o,
		preset: (o.preset as DocumentThemeV1["preset"]) ?? DEFAULT_DOCUMENT_THEME.preset,
		headerLayout: (o.headerLayout as DocumentThemeV1["headerLayout"]) ?? DEFAULT_DOCUMENT_THEME.headerLayout,
		tableDensity: (o.tableDensity as DocumentThemeV1["tableDensity"]) ?? DEFAULT_DOCUMENT_THEME.tableDensity,
	};
}

interface CompanySettings {
	receiptHeader?: string;
	receiptFooter?: string;
	showLogo?: boolean;
	showTax?: boolean;
	showDiscount?: boolean;
	maxShiftsPerPos?: number;
	documentTheme?: DocumentThemeV1;
	storefrontAboutText?: string;
	storefrontFaqText?: string;
	storefrontTermsText?: string;
	storefrontPrivacyText?: string;
	storefrontSupportPhone?: string;
	storefrontSupportEmail?: string;
	storefrontSupportWhatsapp?: string;
	storefrontSupportWebsite?: string;
	storefrontSupportAddress?: string;
	[key: string]: unknown;
}

interface Company {
	id: string;
	name: string;
	description?: string;
	logo?: string;
	website?: string;
	email: string;
	phone: string;
	address: string;
	city?: string;
	postalCode?: string;
	country?: string;
	taxNumber?: string;
	commercialRecord?: string;
	currency?: string;
	timezone?: string;
	settings?: CompanySettings;
	createdAt: string;
	updatedAt: string;
}

const SUPPORTED_CURRENCIES = ["SAR", "AED", "QAR", "KWD", "BHD", "OMR"] as const;

interface UpdateCompanyData {
	name?: string;
	description?: string;
	logo?: string;
	website?: string;
	email?: string;
	phone?: string;
	address?: string;
	city?: string;
	postalCode?: string;
	country?: string;
	taxNumber?: string;
	commercialRecord?: string;
	currency?: string;
	timezone?: string;
	settings?: CompanySettings;
}

export default function CompanyPage() {
	const { t } = useTranslation();
	const { toast } = useToast();
	const canCompanyUpdate = usePermission("company.update");
	const [company, setCompany] = useState<Company | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const editing = canCompanyUpdate && !saving;
	const [companyForm, setCompanyForm] = useState<UpdateCompanyData>({});
	const logoInputRef = useRef<HTMLInputElement>(null);
	const [logoUploading, setLogoUploading] = useState(false);
	/** Local preview (data URL) while upload is in flight */
	const [logoPreviewDataUrl, setLogoPreviewDataUrl] = useState<string | null>(null);

	// Deep links from the old /branches and /subscription routes (?goto=...)
	const [searchParams] = useSearchParams();
	const [settingsSection, setSettingsSection] = useState(() =>
		["entity", "branches", "documents", "receipts", "subscription"].includes(searchParams.get("goto") || "")
			? searchParams.get("goto")!
			: "entity",
	);
	useEffect(() => {
		setSettingsSection(
			["entity", "branches", "documents", "receipts", "subscription"].includes(searchParams.get("goto") || "")
				? searchParams.get("goto")!
				: "entity",
		);
	}, [searchParams]);
	const branchesRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (loading) return;
		const goto = searchParams.get("goto");
		const ref = goto === "branches" ? branchesRef : null;
		if (!ref) return;
		// Child sections load their own data and shift the layout, so keep
		// snapping to the target until its position is stable.
		let lastTop: number | null = null;
		const interval = setInterval(() => {
			const el = ref.current;
			if (!el) return;
			const top = el.getBoundingClientRect().top;
			if (lastTop !== null && Math.abs(top - lastTop) < 2) {
				clearInterval(interval);
				return;
			}
			lastTop = top;
			el.scrollIntoView({ behavior: "auto", block: "start" });
		}, 250);
		const stop = setTimeout(() => clearInterval(interval), 4000);
		return () => {
			clearInterval(interval);
			clearTimeout(stop);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading]);

	// Receipt settings (from company profile)
	const [receiptHeader, setReceiptHeader] = useState("");
	const [receiptFooter, setReceiptFooter] = useState("");
	const [showLogo, setShowLogo] = useState(true);
	const [showTax, setShowTax] = useState(true);
	const [showDiscount, setShowDiscount] = useState(true);
	// Open-shift limit is resolved from the active subscription plan by the backend.
	const [maxShiftsPerPos, setMaxShiftsPerPos] = useState(1);

	// Business currency is stored on the company profile.
	const [systemCurrency, setSystemCurrency] = useState("SAR");
	const [documentTheme, setDocumentTheme] = useState<DocumentThemeV1>(DEFAULT_DOCUMENT_THEME);

	// Loaded values establish the baseline; only user changes show Save/Cancel.
	const { isDirty, markSaved } = useIsDirty(
		{
			companyForm,
			receiptHeader,
			receiptFooter,
			showLogo,
			showTax,
			showDiscount,
			maxShiftsPerPos,
			systemCurrency,
			documentTheme,
		},
		!loading && company !== null,
	);
	useUnsavedChangesGuard(canCompanyUpdate && isDirty && !saving);

	useEffect(() => {
		fetchCompanyProfile();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function hydrateReceiptFromProfile(response: Company) {
		const s = response.settings ?? {};
		setReceiptHeader((s.receiptHeader as string) || "");
		setReceiptFooter((s.receiptFooter as string) || "");
		setShowLogo(s.showLogo !== undefined ? s.showLogo : true);
		setShowTax(s.showTax !== undefined ? s.showTax : true);
		setShowDiscount(s.showDiscount !== undefined ? s.showDiscount : true);
		setMaxShiftsPerPos(typeof s.maxShiftsPerPos === "number" ? s.maxShiftsPerPos : 1);
		setDocumentTheme(mergeDocumentTheme((s as { documentTheme?: unknown }).documentTheme));
		setSystemCurrency(response.currency || (s.systemCurrency as string) || "SAR");
	}

	function hydrateCompanyForm(response: Company) {
		setCompanyForm({
			name: response.name,
			description: response.description,
			logo: response.logo,
			website: response.website,
			email: response.email,
			phone: response.phone,
			address: response.address,
			city: response.city,
			postalCode: response.postalCode,
			country: response.country,
			taxNumber: response.taxNumber,
			commercialRecord: response.commercialRecord,
			currency: response.currency,
		});
		hydrateReceiptFromProfile(response);
	}

	const fetchCompanyProfile = async () => {
		try {
			setLoading(true);
			const response = await companyAPI.getProfile();
			setCompany(response);
			hydrateCompanyForm(response);
			if (company) markSaved();
		} catch (error: any) {
			if (error?.response?.status === 404 || error?.message?.includes("not found")) {
				setCompany(null);
			} else {
				toast({
					title: t("common.error"),
					description: t("company.errors.fetchFailed"),
					variant: "destructive",
				});
			}
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		if (!company || !canCompanyUpdate || !isDirty || saving || logoUploading) return;

		try {
			setSaving(true);
			const storedSettings = { ...(company.settings ?? {}) };
			delete storedSettings.maxPosPerBranch;
			const nextSettings: CompanySettings = {
				...storedSettings,
				receiptHeader,
				receiptFooter,
				showLogo,
				showTax,
				showDiscount,
				maxShiftsPerPos: Math.max(1, Math.floor(maxShiftsPerPos) || 1),
			};
			if (canCompanyUpdate) {
				nextSettings.documentTheme = { ...documentTheme, version: 1 };
			}
			const payload: UpdateCompanyData = {
				...companyForm,
				currency: systemCurrency,
				settings: nextSettings,
			};
			await companyAPI.updateProfile(payload);
			toast({
				title: t("common.success"),
				description: t("company.messages.updated"),
			});
			markSaved();
			fetchCompanyProfile();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("company.errors.updateFailed"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file) return;

		if (!file.type.match(/^image\/(jpeg|jpg|png|gif)$/i)) {
			toast({
				title: t("common.error"),
				description: t("company.errors.invalidLogoType"),
				variant: "destructive",
			});
			return;
		}
		if (file.size > 5 * 1024 * 1024) {
			toast({
				title: t("common.error"),
				description: t("company.errors.logoTooLarge"),
				variant: "destructive",
			});
			return;
		}

		const reader = new FileReader();
		reader.onload = ev => {
			setLogoPreviewDataUrl((ev.target?.result as string) ?? null);
		};
		reader.readAsDataURL(file);

		setLogoUploading(true);
		try {
			const result = (await companyAPI.uploadLogo(file)) as { url?: string };
			if (!result?.url) {
				throw new Error("No logo URL returned");
			}
			const baseUrl = (process.env.REACT_APP_API_URL || "http://localhost:3001/api").replace(/\/api\/?$/, "");
			const fullUrl = result.url.startsWith("http") ? result.url : `${baseUrl}${result.url}`;
			setCompanyForm(prev => ({ ...prev, logo: fullUrl }));
			setLogoPreviewDataUrl(null);
			toast({
				title: t("common.success"),
				description: t("company.messages.logoUploaded"),
			});
		} catch (error) {
			setLogoPreviewDataUrl(null);
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("company.errors.logoUploadFailed"),
				variant: "destructive",
			});
		} finally {
			setLogoUploading(false);
		}
	};

	const handleLogoRemove = () => {
		setCompanyForm(prev => ({ ...prev, logo: undefined }));
		setLogoPreviewDataUrl(null);
	};

	if (loading) return <Loading />;

	if (!company) {
		return (
			<div className="container mx-auto p-6">
				<div className="text-center py-8 space-y-4">
					<Building2 className="h-12 w-12 mx-auto text-muted-foreground" />
					<h2 className="text-xl font-semibold">{t("company.errors.notFound")}</h2>
					<p className="text-muted-foreground">{t("company.messages.noCompanyMessage")}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="settings-workspace container mx-auto p-6 space-y-6">
			<PageHeading>{t("navigation.storeData")}</PageHeading>

			<nav className="workspace-tabs" aria-label={t("workspace.allSettings")}>
				{[
					["entity", "company.sections.entity"],
					["branches", "navigation.branches"],
					["documents", "company.sections.a4Documents"],
					["receipts", "company.sections.receipts"],
					["subscription", "navigation.subscription"],
				].map(([id, label]) => (
					<button
						key={id}
						id={`settings-tab-${id}`}
						type="button"
						aria-current={settingsSection === id ? "page" : undefined}
						aria-controls={`settings-${id}`}
						onClick={() => setSettingsSection(id)}
					>
						{t(label)}
					</button>
				))}
			</nav>
			<section
				id="settings-entity"
				aria-labelledby="settings-tab-entity"
				hidden={settingsSection !== "entity"}
				className="space-y-4"
			>
				<SettingsSurface>
					<div className="business-logo-row">
						<div className="business-logo-preview">
							{resolveCompanyLogoSrc(logoPreviewDataUrl || companyForm.logo) ? (
								<img src={resolveCompanyLogoSrc(logoPreviewDataUrl || companyForm.logo)} alt="" />
							) : (
								<Building2 className="h-8 w-8 text-primary" />
							)}
							{logoUploading && <Loader2 className="absolute h-7 w-7 animate-spin" />}
						</div>
						<div className="min-w-0 space-y-2">
							<h2 className="text-sm font-semibold">{t("company.fields.logo")}</h2>
							<p className="text-xs text-muted-foreground">{t("company.hints.logoUpload")}</p>
							{editing && (
								<div className="flex flex-wrap gap-2">
									<input
										ref={logoInputRef}
										id="company-logo-file"
										type="file"
										accept="image/jpeg,image/jpg,image/png,image/gif"
										className="sr-only"
										onChange={handleLogoFileChange}
										disabled={logoUploading}
										aria-label={t("company.fields.logo")}
									/>
									<Button
										type="button"
										variant="outline"
										size="sm"
										disabled={logoUploading}
										onClick={() => logoInputRef.current?.click()}
									>
										<Upload className="h-4 w-4 me-2" />
										{t("company.actions.chooseLogo")}
									</Button>
									{(companyForm.logo || logoPreviewDataUrl) && (
										<Button
											type="button"
											variant="ghost"
											size="sm"
											disabled={logoUploading}
											onClick={handleLogoRemove}
										>
											<Trash2 className="h-4 w-4 me-2" />
											{t("common.delete")}
										</Button>
									)}
								</div>
							)}
						</div>
					</div>
					<div className="grid gap-x-5 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
						{(
							[
								"name",
								"description",
								"website",
								"email",
								"phone",
								"taxNumber",
								"commercialRecord",
							] as const
						).map(key => (
							<div key={key} className="space-y-2 min-w-0">
								<Label htmlFor={key}>
									{t(`company.fields.${key}`)}
									{key === "name" && <span className="text-destructive"> *</span>}
								</Label>
								<Input
									id={key}
									type={key === "email" ? "email" : "text"}
									dir={
										["email", "phone", "website", "taxNumber", "commercialRecord"].includes(key)
											? "ltr"
											: undefined
									}
									className="rtl:text-right"
									readOnly={!editing}
									value={companyForm[key] || ""}
									placeholder={t(`company.placeholders.${key}`)}
									onChange={e => setCompanyForm(prev => ({ ...prev, [key]: e.target.value }))}
								/>
							</div>
						))}
						<div className="space-y-2">
							<Label>{t("settings.system.currency")}</Label>
							<Select value={systemCurrency} disabled={!editing} onValueChange={setSystemCurrency}>
								<SelectTrigger aria-label={t("settings.system.currency")}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{SUPPORTED_CURRENCIES.map(code => (
										<SelectItem key={code} value={code}>
											{t(`currencies.${code}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<div className="mt-6 border-t pt-5">
						<h2 className="mb-4 text-sm font-semibold">{t("company.location.title")}</h2>
						<div className="grid gap-5 sm:grid-cols-3">
							<div className="space-y-2">
								<Label>{t("company.fields.country")}</Label>
								<Select
									value={companyForm.country}
									disabled={!editing}
									onValueChange={country => setCompanyForm(prev => ({ ...prev, country }))}
								>
									<SelectTrigger aria-label={t("company.fields.country")}>
										<SelectValue placeholder={t("company.placeholders.country")} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="Saudi Arabia">{t("countries.saudiArabia")}</SelectItem>
									</SelectContent>
								</Select>
							</div>
							{(["city", "postalCode", "address"] as const).map(key => (
								<div key={key} className={key === "address" ? "space-y-2 sm:col-span-3" : "space-y-2"}>
									<Label htmlFor={key}>{t(`company.fields.${key}`)}</Label>
									<Input
										id={key}
										readOnly={!editing}
										value={companyForm[key] || ""}
										placeholder={t(`company.placeholders.${key}`)}
										onChange={e => setCompanyForm(prev => ({ ...prev, [key]: e.target.value }))}
									/>
								</div>
							))}
						</div>
					</div>
				</SettingsSurface>
			</section>
			<section
				id="settings-branches"
				aria-labelledby="settings-tab-branches"
				hidden={settingsSection !== "branches"}
			>
				<div ref={branchesRef} className="scroll-mt-4 space-y-4">
					<BranchesSection />
				</div>
			</section>
			{(["documents", "receipts"] as const).map(section => (
				<section
					key={section}
					id={`settings-${section}`}
					aria-labelledby={`settings-tab-${section}`}
					hidden={settingsSection !== section}
					className="space-y-4"
				>
					<PrintSettings
						mode={section === "documents" ? "document" : "receipt"}
						theme={documentTheme}
						setTheme={setDocumentTheme}
						disabled={!editing}
						name={companyForm.name}
						address={companyForm.address}
						taxNumber={companyForm.taxNumber}
						logo={resolveCompanyLogoSrc(logoPreviewDataUrl || companyForm.logo)}
						currency={systemCurrency}
						receiptHeader={receiptHeader}
						receiptFooter={receiptFooter}
						showLogo={showLogo}
						showTax={showTax}
						showDiscount={showDiscount}
						setReceiptHeader={setReceiptHeader}
						setReceiptFooter={setReceiptFooter}
						setShowLogo={setShowLogo}
						setShowTax={setShowTax}
						setShowDiscount={setShowDiscount}
						onReset={async () => {
							if (await confirmAction(t("company.documents.resetTheme"), { destructive: false }))
								setDocumentTheme(DEFAULT_DOCUMENT_THEME);
						}}
					/>
				</section>
			))}
			<section
				id="settings-subscription"
				aria-labelledby="settings-tab-subscription"
				hidden={settingsSection !== "subscription"}
			>
				<div className="space-y-4">
					<SubscriptionSection />
				</div>
			</section>
			{canCompanyUpdate && settingsSection !== "subscription" && (
				<div className="flex justify-end pt-2">
					<Button id="company-save" onClick={handleSave} disabled={!isDirty || saving || logoUploading}>
						{saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Save className="h-4 w-4 me-2" />}
						{saving ? t("common.saving") : t("common.save")}
					</Button>
				</div>
			)}
		</div>
	);
}
